import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const authHeader = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

export const fetchLandingMetrics = async () => {
  const { data } = await axios.get(`${API}/public/landing-metrics`);
  return data;
};

export const searchPublicBooths = async (query = "") => {
  const q = query ? `?query=${encodeURIComponent(query)}` : "";
  const { data } = await axios.get(`${API}/public/booths${q}`);
  return data;
};

export const loginAdmin = async (payload) => {
  const { data } = await axios.post(`${API}/auth/admin/login`, payload);
  return data;
};

export const loginSupport = async (payload) => {
  const { data } = await axios.post(`${API}/auth/support/login`, payload);
  return data;
};

export const loginOrgAdmin = async (payload) => {
  const { data } = await axios.post(`${API}/auth/orgadmin/login`, payload);
  return data;
};

export const loginBoothIncharge = async (payload) => {
  const { data } = await axios.post(`${API}/auth/booth/login`, payload);
  return data;
};

export const registerApplicant = async (payload) => {
  const { data } = await axios.post(`${API}/auth/applicant/register`, payload);
  return data;
};

export const loginApplicant = async (payload) => {
  const { data } = await axios.post(`${API}/auth/applicant/login`, payload);
  return data;
};

export const requestApplicantOtp = async (payload) => {
  const { data } = await axios.post(`${API}/auth/applicant/request-otp`, payload);
  return data;
};

export const loginApplicantOtp = async (payload) => {
  const { data } = await axios.post(`${API}/auth/applicant/login-otp`, payload);
  return data;
};

export const changePassword = async (token, payload) => {
  const { data } = await axios.post(`${API}/auth/change-password`, payload, authHeader(token));
  return data;
};

export const logoutAuth = async (token) => {
  const { data } = await axios.post(`${API}/auth/logout`, {}, authHeader(token));
  return data;
};

export const getAuthMe = async (token) => {
  const { data } = await axios.get(`${API}/auth/me`, authHeader(token));
  return data;
};

export const getTestUsers = async () => {
  const { data } = await axios.get(`${API}/test-users`);
  return data;
};

export const createOrganization = async (token, payload) => {
  const { data } = await axios.post(`${API}/admin/organizations`, payload, authHeader(token));
  return data;
};

export const listOrganizations = async (token) => {
  const { data } = await axios.get(`${API}/admin/organizations`, authHeader(token));
  return data;
};

export const createBooth = async (token, payload) => {
  const { data } = await axios.post(`${API}/admin/booths`, payload, authHeader(token));
  return data;
};

export const listBooths = async (token, organizationId) => {
  const q = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : "";
  const { data } = await axios.get(`${API}/admin/booths${q}`, authHeader(token));
  return data;
};

export const createRoleUser = async (token, payload) => {
  const { data } = await axios.post(`${API}/admin/users`, payload, authHeader(token));
  return data;
};

export const listRoleUsers = async (token, role) => {
  const q = role ? `?role=${encodeURIComponent(role)}` : "";
  const { data } = await axios.get(`${API}/admin/users${q}`, authHeader(token));
  return data;
};

export const startIntake = async (token, payload) => {
  const { data } = await axios.post(`${API}/intake/start`, payload, authHeader(token));
  return data;
};

export const intakeTurn = async (token, sessionId, payload) => {
  const { data } = await axios.post(`${API}/intake/${sessionId}/turn`, payload, authHeader(token));
  return data;
};

export const finalizeIntake = async (token, payload) => {
  const { data } = await axios.post(`${API}/intake/finalize`, payload, authHeader(token));
  return data;
};

export const getCurrentDashboard = async (token) => {
  const { data } = await axios.get(`${API}/dashboard/current`, authHeader(token));
  return data;
};

export const getAdminDashboard = async (token) => {
  const { data } = await axios.get(`${API}/admin/dashboard`, authHeader(token));
  return data;
};

export const getSupportDashboard = async (token) => {
  const { data } = await axios.get(`${API}/support/dashboard`, authHeader(token));
  return data;
};

export const getOrgDashboard = async (token) => {
  const { data } = await axios.get(`${API}/org/dashboard`, authHeader(token));
  return data;
};

export const getBoothDashboard = async (token) => {
  const { data } = await axios.get(`${API}/booth/dashboard`, authHeader(token));
  return data;
};

export const listComplaints = async (token, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, value);
    }
  });
  const query = params.toString();
  const { data } = await axios.get(`${API}/complaints${query ? `?${query}` : ""}`, authHeader(token));
  return data;
};

export const getComplaintById = async (token, complaintId) => {
  const { data } = await axios.get(`${API}/complaints/${complaintId}`, authHeader(token));
  return data;
};

export const updateComplaintStatus = async (token, complaintId, payload) => {
  const { data } = await axios.post(
    `${API}/complaints/${complaintId}/status`,
    payload,
    authHeader(token),
  );
  return data;
};

export const markComplaintUrgent = async (token, complaintId, payload) => {
  const { data } = await axios.post(
    `${API}/complaints/${complaintId}/urgent`,
    payload,
    authHeader(token),
  );
  return data;
};

export const markComplaintReceived = async (token, complaintId) => {
  const { data } = await axios.post(
    `${API}/complaints/${complaintId}/received`,
    {},
    authHeader(token),
  );
  return data;
};

export const listComplaintNotes = async (token, complaintId) => {
  const { data } = await axios.get(`${API}/complaints/${complaintId}/notes`, authHeader(token));
  return data;
};

export const addComplaintNote = async (token, complaintId, payload) => {
  const { data } = await axios.post(
    `${API}/complaints/${complaintId}/notes`,
    payload,
    authHeader(token),
  );
  return data;
};

export const listComplaintDocuments = async (token, complaintId) => {
  const { data } = await axios.get(`${API}/complaints/${complaintId}/documents`, authHeader(token));
  return data;
};

export const addComplaintDocument = async (token, complaintId, payload) => {
  const { data } = await axios.post(
    `${API}/complaints/${complaintId}/documents`,
    payload,
    authHeader(token),
  );
  return data;
};

export const getComplaintPdfUrl = (complaintId, token) =>
  `${API}/complaints/${complaintId}/pdf?token=${encodeURIComponent(token)}`;
export const getComplaintsCsvUrl = (token) =>
  `${API}/reports/complaints.csv?token=${encodeURIComponent(token)}`;
